import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AlgemeenTab = ({ locatieId, locatieData, onUpdate, isAdmin, magBeheren = true }) => {
  const bedrijfsVeldenGewijzigd = () => Object.entries(bedrijfsData || {}).some(
    ([k, v]) => (locatieData?.[k] || '') !== (v || ''));
  const [formData, setFormData] = useState({
    adres: '',
    postcode: '',
    plaats: '',
    telefoon: '',
    email: '',
    openingstijden: {
      maandag: '',
      dinsdag: '',
      woensdag: '',
      donderdag: '',
      vrijdag: '',
      zaterdag: '',
      zondag: ''
    }
  });
  const [locatieNummer, setLocatieNummer] = useState('');
  const [bedrijfsData, setBedrijfsData] = useState({ kvk: '', btw: '', iban: '', email_facturatie: '' });
  const [bovemijNummer, setBovemijNummer] = useState('');
  const [savingBovemij, setSavingBovemij] = useState(false);
  const [savingNummer, setSavingNummer] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (locatieData?.algemeen) {
      setFormData({
        adres: locatieData.algemeen.adres || '',
        postcode: locatieData.algemeen.postcode || '',
        plaats: locatieData.algemeen.plaats || '',
        telefoon: locatieData.algemeen.telefoon || '',
        email: locatieData.algemeen.email || '',
        openingstijden: {
          maandag: locatieData.algemeen.openingstijden?.maandag || '',
          dinsdag: locatieData.algemeen.openingstijden?.dinsdag || '',
          woensdag: locatieData.algemeen.openingstijden?.woensdag || '',
          donderdag: locatieData.algemeen.openingstijden?.donderdag || '',
          vrijdag: locatieData.algemeen.openingstijden?.vrijdag || '',
          zaterdag: locatieData.algemeen.openingstijden?.zaterdag || '',
          zondag: locatieData.algemeen.openingstijden?.zondag || ''
        }
      });
    }
    setLocatieNummer(locatieData?.locatie_nummer || '');
    setBovemijNummer(locatieData?.bovemij_klantnummer || '');
    setBedrijfsData({
      kvk: locatieData?.kvk || '',
      btw: locatieData?.btw || '',
      iban: locatieData?.iban || '',
      email_facturatie: locatieData?.email_facturatie || '',
    });
  }, [locatieData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/locaties/${locatieId}/algemeen`, formData);
      // 4.8a: locatienummer en bedrijfsgegevens zijn manager+; anders volgt een
      // 403 ná een half gelukte opslag.
      if (magBeheren) {
        if (locatieNummer !== (locatieData?.locatie_nummer || '')) {
          await axios.put(`${API}/locaties/${locatieId}/locatie-nummer`, { locatie_nummer: locatieNummer });
        }
        await axios.put(`${API}/locaties/${locatieId}/bedrijfsgegevens`, bedrijfsData);
      } else if (bedrijfsVeldenGewijzigd()) {
        // 6.24: geen "opgeslagen" beloven voor velden die niet zijn verstuurd
        toast.info('Bedrijfsgegevens kunnen alleen door een manager worden gewijzigd');
      }
      toast.success('Algemene informatie opgeslagen');
      onUpdate();
    } catch (error) {
      console.error('Error saving algemeen:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOpeningstijdenChange = (day, value) => {
    setFormData(prev => ({
      ...prev,
      openingstijden: {
        ...prev.openingstijden,
        [day]: value
      }
    }));
  };

  // F3b/6.24: alleen het locatienummer en de bedrijfsgegevens zijn manager+.
  // De gewone gegevens (adres, telefoon, openingstijden) blijft een bedrijfsleider
  // gewoon beheren — anders kan die niets meer opslaan.
  const alleenLezen = !magBeheren;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {alleenLezen && (
        <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
          data-testid="algemeen-alleen-lezen">
          Het locatienummer en de bedrijfsgegevens kan alleen een manager wijzigen.
        </p>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Contactgegevens</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="adres">Adres</Label>
              <Input
                id="adres"
                value={formData.adres}
                onChange={(e) => handleChange('adres', e.target.value)}
                placeholder="Straatnaam + huisnummer"
              />
            </div>
            <div>
              <Label htmlFor="postcode">Postcode</Label>
              <Input
                id="postcode"
                value={formData.postcode}
                onChange={(e) => handleChange('postcode', e.target.value)}
                placeholder="1234 AB"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="plaats">Plaats</Label>
              <Input
                id="plaats"
                value={formData.plaats}
                onChange={(e) => handleChange('plaats', e.target.value)}
                placeholder="Plaatsnaam"
              />
            </div>
            <div>
              <Label htmlFor="telefoon">Telefoon</Label>
              <Input
                id="telefoon"
                value={formData.telefoon}
                onChange={(e) => handleChange('telefoon', e.target.value)}
                placeholder="010-1234567"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="locatie@truckwash1.nl"
            />
          </div>

          <div>
            <Label htmlFor="locatie_nummer">Locatienummer</Label>
            <Input
              id="locatie_nummer"
              value={locatieNummer}
              onChange={(e) => setLocatieNummer(e.target.value)}
              placeholder="bv. LOC-001"
              data-testid="locatie-nummer-input"
              disabled={alleenLezen}
            />
            <p className="text-xs text-slate-400 mt-1">Uniek nummer voor externe systemen</p>
          </div>
        </CardContent>
      </Card>

      {/* Bedrijfsgegevens */}
      <Card>
        <CardHeader>
          <CardTitle>Bedrijfsgegevens</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="kvk">KVK-nummer</Label>
              <Input
                id="kvk"
                value={bedrijfsData.kvk}
                onChange={(e) => setBedrijfsData(p => ({ ...p, kvk: e.target.value }))}
                placeholder="12345678"
                data-testid="locatie-kvk"
                disabled={alleenLezen}
              />
            </div>
            <div>
              <Label htmlFor="btw">BTW-nummer</Label>
              <Input
                id="btw"
                value={bedrijfsData.btw}
                onChange={(e) => setBedrijfsData(p => ({ ...p, btw: e.target.value }))}
                placeholder="NL123456789B01"
                data-testid="locatie-btw"
                disabled={alleenLezen}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="iban">IBAN</Label>
            <Input
              id="iban"
              value={bedrijfsData.iban}
              onChange={(e) => setBedrijfsData(p => ({ ...p, iban: e.target.value }))}
              placeholder="NL00 INGB 0000 0000 00"
              data-testid="locatie-iban"
              disabled={alleenLezen}
            />
          </div>
          <div>
            <Label htmlFor="email_facturatie">E-mail facturatie</Label>
            <Input
              id="email_facturatie"
              type="email"
              value={bedrijfsData.email_facturatie}
              onChange={(e) => setBedrijfsData(p => ({ ...p, email_facturatie: e.target.value }))}
              placeholder="facturatie@locatie.nl"
              data-testid="locatie-email-facturatie"
              disabled={alleenLezen}
            />
          </div>
        </CardContent>
      </Card>

      {/* Bovemij Verzekering */}
      <Card>
        <CardHeader>
          <CardTitle>Bovemij Verzekering</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="bovemij_klantnummer">Klantnummer Bovemij</Label>
            {isAdmin ? (
              <>
                <Input
                  id="bovemij_klantnummer"
                  value={bovemijNummer}
                  onChange={(e) => setBovemijNummer(e.target.value)}
                  placeholder="bv. 10062241"
                  data-testid="bovemij-klantnummer-input"
                  className="mt-1"
                  disabled={alleenLezen}
                />
                {bovemijNummer !== (locatieData?.bovemij_klantnummer || '') && (
                  <Button
                    type="button"
                    size="sm"
                    className="mt-2"
                    disabled={savingBovemij}
                    onClick={async () => {
                      setSavingBovemij(true);
                      try {
                        await axios.put(`${API}/locaties/${locatieId}/bovemij`, { bovemij_klantnummer: bovemijNummer });
                        toast.success('Bovemij klantnummer opgeslagen');
                        onUpdate();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Fout bij opslaan');
                      } finally {
                        setSavingBovemij(false);
                      }
                    }}
                    data-testid="bovemij-save-btn"
                  >
                    {savingBovemij ? 'Opslaan...' : 'Opslaan'}
                  </Button>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-700 bg-slate-50 rounded px-3 py-2 mt-1" data-testid="bovemij-klantnummer-readonly">
                {bovemijNummer || <span className="text-slate-400 italic">Niet ingesteld</span>}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Openingstijden</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'].map((day) => (
            <div key={day} className="grid grid-cols-4 gap-4 items-center">
              <Label className="capitalize">{day}</Label>
              <Input
                className="col-span-3"
                value={formData.openingstijden[day]}
                onChange={(e) => handleOpeningstijdenChange(day, e.target.value)}
                placeholder="07:00 - 21:00 of Gesloten"
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving}>
          {saving ? 'Opslaan...' : 'Opslaan'}
        </Button>
      </div>
    </form>
  );
};

export default AlgemeenTab;
